'use client'
import { createContext, useCallback, useContext, useSyncExternalStore } from 'react'

export const VAT_COOKIE_NAME = 'vat_exclude_pref'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365
const CHANGE_EVENT = 'vat-pref:change'

const readCookie = (): boolean => {
  if (typeof document === 'undefined') return false
  const match = new RegExp(`(?:^|;\\s*)${VAT_COOKIE_NAME}=([^;]*)`).exec(document.cookie)
  return match?.[1] === 'true'
}

const writeCookie = (value: boolean) => {
  document.cookie = `${VAT_COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
}

const subscribe = (callback: () => void) => {
  window.addEventListener(CHANGE_EVENT, callback)
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback)
  }
}

// Server-rendered components can't read `document.cookie`, so the page reads
// the cookie via `next/headers`, then wraps the tree in this provider so the
// hook's server snapshot and the first client render agree. Without it, SSR
// always rendered gross prices and switched after hydration.
const VatPreferenceContext = createContext<boolean>(false)

export const VatPreferenceProvider = VatPreferenceContext.Provider

/**
 * Persistent FE preference for rendering prices excluding VAT.
 * Backed by a same-site cookie so the server can read it too.
 */
export const useVatPreference = (): {
  excludeVat: boolean
  setExcludeVat: (value: boolean) => void
} => {
  const ssrValue = useContext(VatPreferenceContext)
  const excludeVat = useSyncExternalStore(subscribe, readCookie, () => ssrValue)

  const setExcludeVat = useCallback((value: boolean) => {
    writeCookie(value)
    window.dispatchEvent(new Event(CHANGE_EVENT))
  }, [])

  return { excludeVat, setExcludeVat }
}
